import { registerSW } from "virtual:pwa-register";
import { setBootOverlayMode } from "./pwaBootOverlayMode";
import {
  consumePostSwReloadFromSession,
  markPostSwReloadPending,
} from "./postSwReloadSession";
import { setPwaBootUpdate, type PwaBootUpdateUi } from "./pwaBootUpdateStatus";

/** Научно-канцелярские подписи для boot-строки обновления. */
const LABEL_CHECKING = "проверка целостности дистрибутива";
const LABEL_CURRENT = "верификация ревизии клиента";
const LABEL_DEPLOYING = "синхронизация клиентского модуля";
const LABEL_SKIPPED = "верификация ревизии клиента";

let settleTimer: number | undefined;
let sawUpdateFound = false;

/**
 * Пока новый SW в состоянии installing/activating, vite-plugin-pwa (autoUpdate) по событию
 * workbox `activated` вызовет `location.reload()`. Если к этому моменту уже сработал hideBoot,
 * пользователь видит мессенджер и через секунды снова загрузочный экран. Блокируем снятие boot
 * до ухода в reload или сброса (ошибка / таймаут).
 */
let bootDismissBlockedBySwUpdate = false;
let bootDismissSafetyTimer: number | undefined;

function setSwUpdateBlocking(next: boolean) {
  bootDismissBlockedBySwUpdate = next;
  if (bootDismissSafetyTimer !== undefined) {
    window.clearTimeout(bootDismissSafetyTimer);
    bootDismissSafetyTimer = undefined;
  }
  if (next) {
    bootDismissSafetyTimer = window.setTimeout(() => {
      bootDismissBlockedBySwUpdate = false;
      bootDismissSafetyTimer = undefined;
    }, 120_000);
  }
}

/** Экспорт для AuthContext: не скрывать BootScreen, пока висит обновление SW. */
export function swUpdateBlocksBootDismiss(): boolean {
  return bootDismissBlockedBySwUpdate;
}

function attachInstallingFailSafe(registration: ServiceWorkerRegistration) {
  const nw = registration.installing;
  if (!nw) return;
  nw.addEventListener("statechange", () => {
    if (nw.state === "redundant") {
      setSwUpdateBlocking(false);
    }
  });
}

function clearSettleTimer() {
  if (settleTimer !== undefined) {
    window.clearTimeout(settleTimer);
    settleTimer = undefined;
  }
}

function settleCurrentIfStillChecking() {
  setPwaBootUpdate((prev) => {
    if (prev.phase !== "checking") return prev;
    return {
      phase: "current",
      detail: "актуальная сборка, обновления не требуются",
    };
  });
}

/**
 * Регистрация PWA + обновление статуса первой строки BootScreen.
 * В `registerType: "autoUpdate"` колбэк `onNeedRefresh` не используется — ориентируемся на `updatefound` / `onOfflineReady`.
 */
export function registerPwaForBootScreen(): void {
  setSwUpdateBlocking(false);

  if (consumePostSwReloadFromSession()) {
    setBootOverlayMode("post-sw-restart");
    setPwaBootUpdate({
      phase: "current",
      detail: "клиент обновлён",
    });
  } else {
    setBootOverlayMode("full");
    setPwaBootUpdate({
      phase: "checking",
      detail: "ожидание ответа репозитория",
    });
  }
  sawUpdateFound = false;

  registerSW({
    immediate: true,
    onRegisteredSW(_swScriptUrl, registration) {
      clearSettleTimer();

      if (!registration) {
        setSwUpdateBlocking(false);
        setBootOverlayMode("full");
        setPwaBootUpdate({
          phase: "skipped",
          detail: "режим без service worker",
        });
        return;
      }

      if (registration.installing) {
        sawUpdateFound = true;
        markPostSwReloadPending();
        setSwUpdateBlocking(true);
        setBootOverlayMode("sw-update-only");
        attachInstallingFailSafe(registration);
        setPwaBootUpdate({
          phase: "deploying",
          detail: "",
        });
      }

      registration.addEventListener("updatefound", () => {
        sawUpdateFound = true;
        clearSettleTimer();
        markPostSwReloadPending();
        setSwUpdateBlocking(true);
        setBootOverlayMode("sw-update-only");
        attachInstallingFailSafe(registration);
        setPwaBootUpdate({
          phase: "deploying",
          detail: "",
        });
      });

      void registration.update().catch(() => {
        /* сеть/офлайн — ниже сработает таймаут или onOfflineReady */
      });

      settleTimer = window.setTimeout(() => {
        settleTimer = undefined;
        if (!sawUpdateFound) settleCurrentIfStillChecking();
      }, 900);
    },
    onOfflineReady() {
      /** Первая установка SW: кэш готов, ревизия актуальна для офлайна. */
      clearSettleTimer();
      if (!sawUpdateFound) {
        setSwUpdateBlocking(false);
        setBootOverlayMode("full");
        setPwaBootUpdate({
          phase: "current",
          detail: "клиентский кэш подготовлен, ревизия актуальна",
        });
      }
    },
    onRegisterError() {
      clearSettleTimer();
      setSwUpdateBlocking(false);
      setBootOverlayMode("full");
      setPwaBootUpdate({
        phase: "skipped",
        detail: "невозможно инициализировать фоновый модуль",
      });
    },
  });
}

export const PWA_BOOT_LABELS = {
  checking: LABEL_CHECKING,
  current: LABEL_CURRENT,
  deploying: LABEL_DEPLOYING,
  skipped: LABEL_SKIPPED,
} as const;

export function pwaBootRowState(phase: PwaBootUpdateUi["phase"]): "pending" | "active" | "done" | "error" {
  switch (phase) {
    case "checking":
    case "deploying":
      return "active";
    case "current":
    case "completed":
      return "done";
    case "skipped":
      return "error";
    default:
      return "pending";
  }
}
